import { useEffect, useRef, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { copy } from '@/content/copy';
import { money } from '@/lib/utils';
import { imageSrc } from '@/lib/images';
import { callFunction, FunctionError } from '@/lib/functions';
import { useCart } from '@/features/cart/store';
import type { Product } from '@/features/products/types';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  catalog: Map<string, Product>;
};

type CheckoutResult = {
  reference: string;
  statusToken: string;
  total: number;
  message?: string;
};

type PaymentState = 'idle' | 'waiting' | 'paid' | 'failed' | 'timeout';

const POLL_INTERVAL = 4000;
const POLL_LIMIT = 30;

export function CartSheet({ open, onOpenChange, catalog }: Props) {
  const lines = useCart((state) => state.lines);
  const changeQty = useCart((state) => state.changeQty);
  const remove = useCart((state) => state.remove);
  const clear = useCart((state) => state.clear);

  const [phase, setPhase] = useState<'cart' | 'done'>('cart');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<CheckoutResult | null>(null);
  const [payment, setPayment] = useState<PaymentState>('idle');
  const clientToken = useRef<string | null>(null);

  const rows = lines
    .map((line) => ({ line, product: catalog.get(line.productId) }))
    .filter((row): row is { line: typeof lines[number]; product: Product } => Boolean(row.product));

  const total = rows.reduce((sum, row) => sum + row.product.price * row.line.qty, 0);

  // Stop polling as soon as the sheet closes or the order settles.
  useEffect(() => {
    if (!result || payment !== 'waiting') return;

    let tries = 0;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const tick = async () => {
      if (cancelled) return;
      tries += 1;

      try {
        const data = await callFunction<{ status: string }>('order-status', {
          method: 'GET',
          query: { token: result.statusToken },
        });
        if (cancelled) return;
        if (data.status === 'paid' || data.status === 'fulfilled') {
          setPayment('paid');
          return;
        }
        if (data.status === 'failed' || data.status === 'cancelled') {
          setPayment('failed');
          return;
        }
      } catch {
        // Transient network failures are expected while polling; keep trying.
      }

      if (cancelled) return;
      if (tries >= POLL_LIMIT) {
        setPayment('timeout');
        return;
      }
      timer = setTimeout(tick, POLL_INTERVAL);
    };

    timer = setTimeout(tick, POLL_INTERVAL);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [result, payment]);

  const reset = () => {
    setPhase('cart');
    setResult(null);
    setPayment('idle');
    setError('');
    clientToken.current = null;
  };

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
    if (!next && phase === 'done') reset();
  };

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!rows.length) {
      setError(copy.checkout.emptyCart);
      return;
    }

    const form = event.currentTarget;
    const data = new FormData(form);

    clientToken.current ??= crypto.randomUUID();
    setSubmitting(true);
    setError('');

    try {
      const response = await callFunction<CheckoutResult>('checkout', {
        body: {
          items: rows.map(({ line }) => ({
            id: line.productId,
            size: line.size,
            qty: line.qty,
          })),
          customer: {
            name: data.get('name'),
            phone: data.get('phone'),
            email: data.get('email'),
            address: data.get('address'),
          },
          clientToken: clientToken.current,
        },
      });

      clear();
      form.reset();
      setResult(response);
      setPayment('waiting');
      setPhase('done');
    } catch (caught) {
      setError(
        caught instanceof FunctionError && caught.status < 500
          ? caught.message
          : copy.checkout.genericError,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const doneCopy = (() => {
    if (payment === 'paid') {
      return { title: copy.payment.receivedTitle, text: copy.payment.receivedText };
    }
    if (payment === 'failed') {
      return { title: copy.payment.failedTitle, text: copy.payment.failedText };
    }
    if (payment === 'timeout') {
      return { title: copy.payment.waitingTitle, text: copy.payment.waitingText };
    }
    return {
      title: copy.cart.thankYou,
      text: result?.message ?? copy.checkout.prompt,
    };
  })();

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="p-6">
        {phase === 'cart' ? (
          <div className="flex h-full flex-col">
            <SheetTitle className="font-serif text-[1.9rem] leading-tight">
              {copy.cart.title}
            </SheetTitle>

            <ul className="my-5 grid gap-4 overflow-y-auto">
              {rows.length === 0 && (
                <li className="text-sm text-brand-muted">{copy.cart.empty}</li>
              )}

              {rows.map(({ line, product }) => (
                <li
                  key={`${line.productId}-${line.size}`}
                  className="grid grid-cols-[64px_1fr_auto] items-start gap-3.5 border-b border-brand-line pb-4 text-sm"
                >
                  {product.images[0] ? (
                    <img
                      src={imageSrc(product.images[0])}
                      alt={product.images[0].alt || product.name}
                      width={64}
                      height={80}
                      className="h-20 w-16 bg-[#111] object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="h-20 w-16 bg-[#111]" />
                  )}

                  <div className="grid gap-1">
                    <strong className="font-serif text-base font-medium">{product.name}</strong>
                    <small className="text-brand-muted">
                      {line.size ? `Size ${line.size}` : copy.product.oneSize}
                    </small>
                    <span className="mt-1 inline-flex items-center gap-2.5">
                      <button
                        type="button"
                        aria-label={copy.cart.oneLess(product.name)}
                        onClick={() => changeQty(line.productId, line.size, -1)}
                        className="grid size-[26px] place-items-center border border-brand-line transition-colors hover:border-gold hover:text-gold"
                      >
                        <Minus className="size-3" />
                      </button>
                      <span aria-live="polite" className="min-w-4 text-center">
                        {line.qty}
                      </span>
                      <button
                        type="button"
                        aria-label={copy.cart.oneMore(product.name)}
                        onClick={() => changeQty(line.productId, line.size, 1)}
                        className="grid size-[26px] place-items-center border border-brand-line transition-colors hover:border-gold hover:text-gold"
                      >
                        <Plus className="size-3" />
                      </button>
                      <button
                        type="button"
                        aria-label={copy.cart.remove(product.name)}
                        onClick={() => remove(line.productId, line.size)}
                        className="ml-1.5 text-[0.78rem] text-brand-muted underline underline-offset-2 hover:text-gold"
                      >
                        {copy.admin.remove}
                      </button>
                    </span>
                  </div>

                  <strong className="font-medium">{money(product.price * line.qty)}</strong>
                </li>
              ))}
            </ul>

            <div className="flex justify-between border-t border-brand-line py-3.5">
              <span>{copy.cart.total}</span>
              <strong>{money(total)}</strong>
            </div>

            <form onSubmit={submit} className="grid gap-3" autoComplete="on">
              <div className="grid gap-1.5">
                <Label htmlFor="f-name">{copy.checkout.name}</Label>
                <Input id="f-name" name="name" required minLength={2} maxLength={80} autoComplete="name" />
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="f-phone">{copy.checkout.phone}</Label>
                <Input
                  id="f-phone"
                  name="phone"
                  type="tel"
                  inputMode="tel"
                  required
                  maxLength={16}
                  autoComplete="tel"
                  placeholder={copy.checkout.phonePlaceholder}
                  pattern="^(?:\+?254|0)?[17][0-9]{8}$"
                  title="Enter a Kenyan mobile number, e.g. 0712 345 678"
                />
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="f-email">{copy.checkout.email}</Label>
                <Input id="f-email" name="email" type="email" maxLength={120} autoComplete="email" />
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="f-address">{copy.checkout.address}</Label>
                <Textarea
                  id="f-address"
                  name="address"
                  required
                  minLength={5}
                  maxLength={200}
                  rows={3}
                  autoComplete="street-address"
                />
              </div>

              <Button type="submit" disabled={submitting || rows.length === 0} className="mt-2 w-full">
                {submitting ? copy.checkout.placing : copy.checkout.pay}
              </Button>

              <p role="status" aria-live="polite" className="min-h-6 text-sm text-gold-soft">
                {error && <span className="text-brand-danger">{error}</span>}
              </p>
            </form>
          </div>
        ) : (
          <div className="m-auto text-center">
            <h3 className="font-serif text-[2rem] leading-tight">{doneCopy.title}</h3>
            <p className="my-3 text-[#cfc6ba]">{doneCopy.text}</p>
            {result && (
              <p className="text-[#cfc6ba]">
                {copy.cart.order} <strong className="text-gold-soft">{result.reference}</strong>
              </p>
            )}

            {payment === 'waiting' && (
              <p className="mt-4 text-sm text-brand-muted" aria-live="polite">
                {copy.payment.waitingTitle}…
              </p>
            )}

            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
              className="mt-6"
              hidden={payment === 'waiting'}
            >
              {copy.cart.continue}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
